export interface ChatMessage {
  id?: number;
  role: 'user' | 'support';
  content: string;
}

/**
 * Merge freshly polled messages into the list already on screen.
 *
 * When someone sends a message we render it immediately, before the server has
 * assigned it an id. The poller then asks for everything after `lastId` and the
 * server hands back its own copy of that same message — so without this, every
 * message the user sent appeared twice about four seconds later.
 *
 * Incoming (server) messages win: an optimistic entry with no id is dropped
 * once a server message with the same role and content arrives, so the entry
 * gains its real id and stays deduplicated on later polls.
 */
export function mergeChatMessages(
  existing: ChatMessage[],
  incoming: ChatMessage[]
): ChatMessage[] {
  if (incoming.length === 0) return existing;

  const seenIds = new Set(
    existing.map(m => m.id).filter((id): id is number => id !== undefined)
  );

  const kept = [...existing];
  const appended: ChatMessage[] = [];

  for (const msg of incoming) {
    // The server can replay an id we already hold (e.g. two pollers sharing a
    // session, or a retried request) — never show it twice.
    if (msg.id !== undefined && seenIds.has(msg.id)) continue;
    if (msg.id !== undefined) seenIds.add(msg.id);

    const optimisticIndex = kept.findIndex(
      m => m.id === undefined && m.role === msg.role && m.content === msg.content
    );
    if (optimisticIndex !== -1) kept.splice(optimisticIndex, 1);

    appended.push(msg);
  }

  return appended.length > 0 ? [...kept, ...appended] : existing;
}
